import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import { Box, RadioGroup, Slider, Typography } from '@mui/material';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import { useLocation } from 'react-router-dom';
import { ValuationApproachModel } from 'api';
import { PDF_EXPORT_INFO_MESSAGE } from 'common/constants/documents';
import { OPM_CALCULATOR_ENABLER } from 'common/constants/features';
import { useStore } from 'common/store';
import { PdfExportConfig, TEMPLATE_CONSTANTS } from 'common/types/pdfExport';
import { UseStoreValues } from 'common/types/store';
import { StyledMobileDatePicker } from 'components/DatePickers';
import useCheckFeatures from 'components/FeatureEnabler/hooks';
import {
  getDefaultConcurrency,
  MAX_CONCURRENCY,
  MIN_CONCURRENCY,
  usePdfExport,
} from 'features/pdfExport';
import { currentISODate, getAllocationStatus, ISO_DATE_FORMAT, Logger } from 'utilities';
import { useIsCompanyPath } from 'utilities/useIsCompanyPath/useIsCompanyPath';
import { CompanySubOptions } from './components/CompanySubOptions';
import { FirmSubOptions } from './components/FirmSubOptions';
import { RadioOption } from './components/RadioOption';
import { useDialogValuationInfo } from './hooks/useDialogValuationInfo';
import { usePdfPages } from './hooks/usePdfPages';
import {
  PdfExportButton,
  StyledButton,
  StyledContentContainer,
  StyledDialog,
  StyledDialogActions,
  StyledDialogContent,
  StyledDialogTitle,
  StyledInfoIcon,
  StyledRadioContainer,
} from './PdfExportConfigDialog.styles';
import {
  COMPANY_OPTION_CONFIGS,
  CompanyPdfApproachOptionKey,
  CompanyPdfOptions,
  DEFAULT_COMPANY_OPTIONS_ORDER_NON_APPROACH,
  DEFAULT_PDF_CONFIGURATION,
  PdfExportConfigDialogProps,
  PdfExportConfiguration,
  PdfExportOption,
} from './PdfExportConfigDialog.types';

const PdfExportConfigDialog = ({ open, onClose }: PdfExportConfigDialogProps) => {
  const [configuration, setConfiguration] = useState<PdfExportConfiguration>(DEFAULT_PDF_CONFIGURATION);
  const [concurrency, setConcurrency] = useState<number>(() => getDefaultConcurrency());
  // '' means the user cleared the issue date.
  const [issueDate, setIssueDate] = useState<string>(currentISODate);
  // Ref, not state — never read during render, so flagging an edit shouldn't re-render.
  const isIssueDateEditedRef = useRef(false);

  // Determine current context
  const isCompanyPath = useIsCompanyPath();
  const isFirmPath = useMemo(() => !isCompanyPath, [isCompanyPath]);

  let coverTitle = 'Valuation of';

  if (isCompanyPath) {
    coverTitle = 'Valuation of';
  } else if (isFirmPath) {
    coverTitle = 'Firm';
  }

  // Get entity information from store
  const [storeValues] = useStore() as unknown as UseStoreValues;
  const { allocationInfo, companyInfo, firmInfo } = storeValues;
  const location = useLocation();

  // The OPM Calculations option follows the calculator itself: hidden while the flag is off.
  const [, checkFeature] = useCheckFeatures();
  const isOPMCalculatorEnabled = checkFeature(OPM_CALCULATOR_ENABLER);

  // Fetch the merged valuation directly so approach availability validation works on every
  // company tab, not only the Valuations page (which is the only place the store is populated).
  const {
    valuationInfo,
    isLoading: isLoadingDialogValuation,
    measurementDate: resolvedMeasurementDate,
    selectedAllocationVersion,
    capTableVersions,
    opmInputSets,
  } = useDialogValuationInfo({
    enabled: open && isCompanyPath,
    // Cap Table and Breakpoints emit one page per referenced version; Fund Ownership and
    // Cash Flow Ledger need the list to pin themselves to the primary version.
    needsCapTableVersions:
      configuration.companyOptions.capTable
      || configuration.companyOptions.breakpointAnalysis
      || configuration.companyOptions.fundOwnership
      || configuration.companyOptions.cashFlowLedger,
    needsOPMInputSets: isOPMCalculatorEnabled,
  });

  const availableOPMInputSets = isOPMCalculatorEnabled ? opmInputSets : undefined;

  // Get the current entity name based on context
  const entityName = useMemo(() => {
    if (isCompanyPath && companyInfo?.name) {
      return companyInfo.name;
    }
    if (isFirmPath && firmInfo?.name) {
      return firmInfo.name;
    }
    return '';
  }, [isCompanyPath, isFirmPath, companyInfo, firmInfo]);

  // Get measurement date from query string, falling back to the date resolved by
  // useDialogValuationInfo. The Prism nav clears the `date` param for "Most Recent",
  // so without the fallback the cover and filename would lose the valuation date.
  const measurementDate = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const raw = searchParams.get('date') ?? '';
    if (raw === '' || raw === 'undefined') {
      return resolvedMeasurementDate ?? '';
    }
    return raw;
  }, [location.search, resolvedMeasurementDate]);

  // Generate dynamic filename based on entity name and date.
  // Target format: "Company Name Valuation as of mm.dd.yyyy"
  const filename = useMemo(() => {
    const parsedDate = measurementDate ? moment(measurementDate) : null;
    const formattedDate = parsedDate?.isValid() ? parsedDate.format('MM.DD.YYYY') : '';

    let name = '';
    if (entityName && formattedDate) {
      name = `${entityName} Valuation as of ${formattedDate}`;
    } else if (entityName) {
      name = `${entityName} Valuation`;
    } else if (formattedDate) {
      name = `Valuation as of ${formattedDate}`;
    }

    // Strip characters that are invalid in filenames while keeping spaces and periods.
    const sanitized = name.replace(/[<>:"/\\|?*]/g, '').trim();

    return sanitized || 'pdf-export';
  }, [entityName, measurementDate]);

  // Cover issue date default:
  //  - FINAL: the day the exported version was marked final (Allocation.last_finalized_at),
  //    falling back to Allocation.updated_at when that stamp is missing (below).
  //  - anything else, or no allocation at all: today, the day the export is created.
  const defaultIssueDate = useMemo(() => {
    const today = currentISODate();
    // No allocation off the company path, or not resolved yet; the effect below re-applies.
    if (!isCompanyPath || !selectedAllocationVersion) {
      return today;
    }
    const {
      is_final: isFinal,
      last_finalized_at: lastFinalizedAt,
      updated_at: updatedAt,
    } = selectedAllocationVersion;
    if (!isFinal) {
      // Draft or published: today, the day the export is created.
      return today;
    }
    // FINAL. `last_finalized_at` is the real answer, but it only exists for versions
    // finalized after the column was added (2026-08-05) — it was never backfilled, so
    // almost every pre-existing FINAL version has none. `updated_at` is the closest
    // available proxy: work on a valuation stops at finalization, so the last write is
    // usually the finalize itself (same day for ~70% of the rows the audit log can date,
    // within three days for ~93%). Approximate on purpose, and the picker below lets the
    // user correct it — a months-old valuation dated today is the worse default.
    const source = lastFinalizedAt ?? updatedAt;
    if (!source) {
      return today;
    }
    // UTC instant -> the calendar day in the viewer's own timezone.
    const finalizedDay = moment(source);
    if (!finalizedDay.isValid()) {
      return today;
    }
    // Clamp: a future stamp (clock skew) would fall outside the picker's maxDate.
    return finalizedDay.isAfter(moment(), 'day') ? today : finalizedDay.format(ISO_DATE_FORMAT);
  }, [isCompanyPath, selectedAllocationVersion]);

  // Re-apply the derived default unless the user picked something. Not a lazy useState
  // initializer: the dialog stays mounted across openings, and the default arrives async.
  useEffect(() => {
    if (!open) {
      isIssueDateEditedRef.current = false;
      return;
    }
    if (isIssueDateEditedRef.current) {
      return;
    }
    setIssueDate(defaultIssueDate);
  }, [open, defaultIssueDate]);

  // Local midnight, so the calendar highlights the intended day. `new Date(issueDate)` would
  // parse as UTC midnight and render the previous day west of Greenwich.
  const issueDateValue = useMemo(
    () => (issueDate ? moment(issueDate, ISO_DATE_FORMAT, true).toDate() : null),
    [issueDate]
  );

  const handleIssueDateChange = useCallback((newValue: unknown) => {
    isIssueDateEditedRef.current = true;
    const next = newValue ? moment(newValue as Date) : null;
    setIssueDate(next?.isValid() ? next.format(ISO_DATE_FORMAT) : '');
  }, []);

  // Get dynamic URLs based on current context and configuration
  const pages = usePdfPages(configuration, valuationInfo, capTableVersions, availableOPMInputSets);

  const availableApproachKeys = useMemo(() => {
    const keys = new Set<CompanyPdfApproachOptionKey>();
    valuationInfo?.valuations_approaches?.forEach(approach => {
      switch (approach.approach_type) {
        case ValuationApproachModel.approach_type.BACKSOLVE:
          if (approach.valuations_approach_backsolve?.applied_methodologies?.length) {
            keys.add('approachBacksolve');
            if (approach.valuations_approach_backsolve?.adjust_for_market) {
              keys.add('approachBacksolveMarketAdjustment');
            }
          }
          break;
        case ValuationApproachModel.approach_type.PUBLIC_COMPS:
          if (approach.valuations_approach_gpc?.gpc_comparison?.length) {
            keys.add('approachPublicComps');
            keys.add('approachPublicCompsPerformanceMetrics');
          }
          break;
        case ValuationApproachModel.approach_type.DISCOUNTED_CASH_FLOW:
          if (approach.valuations_approach_dcf?.dcf_financial_period?.length) {
            keys.add('approachDcf');
          }
          break;
        case ValuationApproachModel.approach_type.TRANSACTION_COMPS:
          if (approach.valuations_approach_gpt?.gpt_transactions?.length) {
            keys.add('approachTransactionComps');            
          }
          break;
        case ValuationApproachModel.approach_type.FUTURE_EXIT:
          if (
            approach.valuations_approach_future_exit?.is_future_exit_calculated
            || approach.valuations_approach_future_exit?.present_share_values?.length
            || approach.valuations_approach_future_exit?.future_share_values?.length
          ) {
            keys.add('approachFutureExit');
          }
          break;
        case ValuationApproachModel.approach_type.SPECIFIED_SHARE_VALUES:
          // The merged page renders an SSV tab for any SSV approach, regardless
          // of whether share_values or cap_table have been populated yet, so
          // mirror that here instead of gating on the nested SSV payload.
          keys.add('approachSpecifiedShareValues');
          break;
        case ValuationApproachModel.approach_type.EXTERNAL_VALUATION:
          if (
            approach.valuations_approach_ev?.equity_value != null
            || approach.valuations_approach_ev?.enterprise_value != null
          ) {
            keys.add('approachExternalValuation');
          }
          break;
        default:
          break;
      }
    });

    // Calibrations live in their own array on the merged valuation, not within
    // valuations_approaches. A calibration tab renders only when the backing GPC
    // approach has comparable companies, so verify that against the related
    // approach (looked up via Calibration.public_comps_approach_calibration).
    const approachesById = new Map(
      valuationInfo?.valuations_approaches?.map(a => [a.id, a]) ?? []
    );
    const hasUsableCalibration = valuationInfo?.calibrations?.some(calibration => {
      const backing = approachesById.get(calibration.public_comps_approach_calibration ?? -1);
      return backing?.valuations_approach_gpc?.gpc_comparison?.length;
    });
    if (hasUsableCalibration) {
      keys.add('approachCalibration');
    }

    // Range sensitivities live in their own array on the merged valuation, similar
    // to calibrations. Surface the option when at least one exists.
    if (valuationInfo?.range_sensitivities?.length) {
      keys.add('approachRangeSensitivity');
    }

    // One OPM Calculations page per distinct OPM input set; the backend already de-duplicated
    // them, so any set at all surfaces the option.
    if (availableOPMInputSets?.length) {
      keys.add('opmCalculations');
    }

    // Public Comps - Descriptions is pinned to the end of the approach list so it
    // sits last in the dialog and the exported PDF.
    if (keys.has('approachPublicComps')) {
      keys.add('approachPublicCompsDescriptions');
    }

    // Transaction Comps - Descriptions is pinned to the end of the approach list so it
    // sits last in the dialog and the exported PDF.
    if (keys.has('approachTransactionComps')) {
      keys.add('approachTransactionCompsDescriptions');
    }

    return keys;
  }, [valuationInfo, availableOPMInputSets]);

  // A cleared issue date must block the export rather than silently drop the cover line.
  const isExportDisabled = pages.length === 0 || isLoadingDialogValuation || !issueDate;

  // Sync companyOptionsOrder when valuation info loads. Only include approach
  // slots whose approach actually exists on the current valuation, so unavailable
  // approaches are hidden from the dialog and excluded from the export.
  useEffect(() => {
    if (!Array.isArray(valuationInfo?.valuations_approaches)) {
      return;
    }
    const nonApproachKeys = COMPANY_OPTION_CONFIGS.map(c => c.key) as Array<keyof CompanyPdfOptions>;
    const validKeys = new Set<keyof CompanyPdfOptions>([...nonApproachKeys, ...availableApproachKeys]);

    setConfiguration(prev => {
      const filtered = prev.companyOptionsOrder.filter(k => validKeys.has(k));
      const available = [...availableApproachKeys];
      const added = available.filter(k => !filtered.includes(k));
      const missing = DEFAULT_COMPANY_OPTIONS_ORDER_NON_APPROACH.filter(k => !filtered.includes(k));
      // A key that becomes available later (OPM Calculations waits on the async feature flag
      // and its own request) goes before the next available key already listed, so it keeps
      // its slot ahead of the pinned Descriptions entries instead of being appended after them.
      const ordered = [...filtered];
      added.forEach(key => {
        const nextListed = available.slice(available.indexOf(key) + 1).find(k => ordered.includes(k));
        const insertAt = nextListed ? ordered.indexOf(nextListed) : ordered.length;
        ordered.splice(insertAt, 0, key);
      });
      const nextOrder = [...ordered, ...missing];
      if (
        nextOrder.length === prev.companyOptionsOrder.length &&
        nextOrder.every((k, i) => k === prev.companyOptionsOrder[i])
      ) {
        return prev;
      }
      return { ...prev, companyOptionsOrder: nextOrder };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableApproachKeys, valuationInfo]);

  const handleCompanyOrderChange = useCallback((order: Array<keyof CompanyPdfOptions>) => {
    setConfiguration(prev => ({ ...prev, companyOptionsOrder: order }));
  }, []);

  // Automatically set the selected option based on current context
  useEffect(() => {
    if (isCompanyPath) {
      setConfiguration(prev => ({ ...prev, selectedOption: PdfExportOption.VALUATIONS }));
    } else if (isFirmPath) {
      setConfiguration(prev => ({ ...prev, selectedOption: PdfExportOption.FIRM }));
    }
  }, [isCompanyPath, isFirmPath]);

  const pdfExport = usePdfExport();

  const handleOptionChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedOption = event.target.value as PdfExportOption;
    setConfiguration(prev => {
      const newConfig = {
        ...prev,
        selectedOption,
      };

      if (selectedOption === PdfExportOption.VALUATIONS) {
        newConfig.companyOptions = {
          valuationSummary: true,
          equityAllocation: true,
          concludedShareValues: true,
          financials: true,
          fundOwnership: true,
          capTable: true,
          breakpointAnalysis: true,
          cashFlowLedger: true,
          approachBacksolve: true,
          approachBacksolveMarketAdjustment: true,
          approachDcf: true,
          approachExternalValuation: true,
          approachFutureExit: true,
          approachPublicComps: true,
          approachPublicCompsPerformanceMetrics: true,
          approachPublicCompsDescriptions: false,
          approachTransactionComps: true,
          approachTransactionCompsDescriptions: true,
          approachSpecifiedShareValues: true,
          approachCalibration: true,
          approachRangeSensitivity: true,
          opmCalculations: true,
        };
      }

      return newConfig;
    });
  }, []);

  const handleCompanyOptionsChange = useCallback(
    (options: typeof configuration.companyOptions) => {
      setConfiguration(prev => ({
        ...prev,
        companyOptions: options,
      }));
    },
    [configuration]
  );

  const handleFirmOptionsChange = useCallback(
    (options: typeof configuration.firmOptions) => {
      setConfiguration(prev => ({
        ...prev,
        firmOptions: options,
      }));
    },
    [configuration]
  );

  const handleExport = useCallback(() => {
    if (firmInfo) {
      const firmName = firmInfo.name;
      const isFirmOwned = valuationInfo?.is_firm_owned || false;
      // Strict check: firm and fund exports have no valuation and must not get the
      // Scalar-owned valuation disclaimer even though they share the Scalar cover.
      const isScalarOwnedValuation = valuationInfo?.is_firm_owned === false;

      // A firm-owned export is branded with the firm's own logo. When the firm has no
      // logo we send an empty source rather than defaulting to Scalar's logo, which
      // would misattribute the valuation; the exporter then stamps the firm name in the
      // cover and header brand slots instead.
      const firmLogo = firmInfo.logo_url || '';
      const coverBackgroundImage = isFirmOwned
        ? TEMPLATE_CONSTANTS.COVER_LIGHT_BACKGROUND_JPG
        : TEMPLATE_CONSTANTS.COVER_BLUE_BACKGROUND_JPG;
      const coverLogo = isFirmOwned ? firmLogo : TEMPLATE_CONSTANTS.SCALAR_WHITE_LOGO_PNG;
      const coverSecondaryLogo = TEMPLATE_CONSTANTS.SCALAR_BLUE_LOGO_PNG;
      const pageLogo = isFirmOwned ? firmLogo : TEMPLATE_CONSTANTS.SCALAR_BLUE_LOGO_PNG;
      const pageBackgroundImage = TEMPLATE_CONSTANTS.PAGES_BACKGROUND_JPG;
      const infoIcon = TEMPLATE_CONSTANTS.INFO_ICON_PNG;

      // Prefer the allocation from the freshly fetched merged valuation (the source of
      // truth this dialog already drives every other option from). The store's
      // allocationInfo is only populated on the Valuations page, so on other company
      // tabs — or when exporting a different version — it is stale/empty and the footer
      // would fall back to "Draft" even when the valuation is Final.
      const statusAllocation = valuationInfo?.allocation ?? allocationInfo;

      let allocationStatus: number | undefined;
      if (statusAllocation) {
        const { is_final: isFinal, is_published: isPublished } = statusAllocation;
        allocationStatus = getAllocationStatus({ isFinal: Boolean(isFinal), isPublished: Boolean(isPublished) });
      }

      const config: PdfExportConfig = {
        filename,
        entityName,
        coverTitle,
        issueDate,
        pages,
        isFirmOwned,
        isScalarOwnedValuation,
        coverBackgroundImage,
        coverLogo,
        coverSecondaryLogo,
        pageBackgroundImage,
        pageLogo,
        infoIcon,
        firmName,
        allocationStatus,
        measurementDate,
        concurrency,
      };

      onClose();
      pdfExport.start(config);
    } else {
      Logger.error(
        'Failed to export PDF: No valuation info or firm info found',
        new Error('Failed to export PDF: No valuation info or firm info found')
      );
      enqueueSnackbar('Failed to export PDF: No valuation info or firm info found', {
        variant: 'error',
        autoHideDuration: 5000,
      });
    }
  }, [
    onClose,
    pages,
    filename,
    entityName,
    firmInfo,
    valuationInfo,
    coverTitle,
    allocationInfo,
    measurementDate,
    issueDate,
    pdfExport,
    concurrency,
  ]);

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <StyledDialogTitle>{`${entityName} PDF Export`}</StyledDialogTitle>

      <StyledDialogContent>
        <StyledRadioContainer>
          <RadioGroup value={configuration.selectedOption} onChange={handleOptionChange}>
            {isCompanyPath && (
              <>
                <RadioOption value={PdfExportOption.VALUATIONS} label="Valuations" />
                <CompanySubOptions
                  options={configuration.companyOptions}
                  onOptionsChange={handleCompanyOptionsChange}
                  showCondition={configuration.selectedOption === PdfExportOption.VALUATIONS}
                  optionsOrder={configuration.companyOptionsOrder}
                  onOrderChange={handleCompanyOrderChange}
                />
              </>
            )}

            {isFirmPath && (
              <>
                <RadioOption value={PdfExportOption.FIRM} label="Firm" />
                <FirmSubOptions
                  options={configuration.firmOptions}
                  onOptionsChange={handleFirmOptionsChange}
                  showCondition={configuration.selectedOption === PdfExportOption.FIRM}
                />
              </>
            )}
          </RadioGroup>
        </StyledRadioContainer>

        <Box mb={3}>
          <Typography variant="subtitle2" mb={1}>
            Issue date
          </Typography>
          <StyledMobileDatePicker
            value={issueDateValue}
            onChange={handleIssueDateChange}
            format="MM/dd/yyyy"
            maxDate={new Date()}
            slotProps={{
              textField: {
                id: 'pdf-export-issue-date',
                size: 'small',
                sx: { width: 180 },
                inputProps: {
                  'data-testid': 'pdf-export-issue-date',
                },
              },
            }}
          />
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            Shown on the cover page. Defaults to the date this valuation was marked final, or today for
            a draft.
          </Typography>
        </Box>

        <Box mb={3}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle2">Parallel page captures</Typography>
            <Typography variant="body2" color="text.secondary">
              {concurrency}
            </Typography>
          </Box>
          <Slider
            id="pdf-export-concurrency-slider"
            value={concurrency}
            min={MIN_CONCURRENCY}
            max={MAX_CONCURRENCY}
            step={1}
            marks
            valueLabelDisplay="auto"
            onChange={(_event, value) => {
              if (typeof value === 'number') {
                setConcurrency(value);
              }
            }}
            data-testid="pdf-export-concurrency-slider"
          />
          <Typography variant="caption" color="text.secondary">
            One page at a time is the most reliable. Raising this exports faster but can drop pages
            under load.
          </Typography>
        </Box>

        <StyledContentContainer>
          <StyledInfoIcon>
            <InfoIcon color="action" />
          </StyledInfoIcon>
          <Typography>{PDF_EXPORT_INFO_MESSAGE}</Typography>
        </StyledContentContainer>
      </StyledDialogContent>

      <StyledDialogActions>
        <StyledButton onClick={onClose}>Cancel</StyledButton>
        <PdfExportButton onClick={handleExport} disabled={isExportDisabled} isExporting={false}>
          Export PDF
        </PdfExportButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default PdfExportConfigDialog;
